import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { string, bool, arrayOf, shape, objectOf, number } from "prop-types";

import cx from "classnames";

import initialState from "./initialState.js";

import fetchSearchResults from "../../common/fetchSearchResults.js";
import {
  toggleFacet,
  clearFacetGroup,
  initFacets,
  initCurrentParams,
  createQueryString,
} from "../../common/dynamicSearch.js";

import { scrollToId } from "../../common/scrollHelpers.js";

import CaseStudyGrid from "./CaseStudyGrid/CaseStudyGrid.jsx";
import FeaturedPromo from "../FeaturedPromo/FeaturedPromo.jsx";
import FilterBar from "./FilterBar/FilterBar.jsx";
import FullGrid from "./FullGrid/FullGrid.jsx";
import ResultsInfo from "./ResultsInfo/ResultsInfo.jsx";
import SimpleGrid from "./SimpleGrid/SimpleGrid.jsx";
import ThreeUpGrid from "./ThreeUpGrid/ThreeUpGrid.jsx";
import SimpleList from "./SimpleList/SimpleList.jsx";
import Sorters from "./Sorters/Sorters.jsx";
import LoadingSpinner from "./LoadingSpinner/LoadingSpinner.jsx";

const propTypes = {
  id: string.isRequired,
  theme: number,
  title: string,
  subtitle: string,
  enableQueryString: bool,
  pagination: bool,
  loadMore: bool,
  hideMobileSummary: bool,
  url: string.isRequired,
  featuredBackground: string,
  dictionary: objectOf(string).isRequired,
  query: shape({
    listingId: string,
    showSummaries: bool,
    showDates: bool,
    showContentTypes: bool,
    showImages: string,
    excludedTaxonomy: string,
    loadAllPages: bool,
    pageId: string,
  }).isRequired,
  link: shape({ href: string, text: string, title: string }),
  sorters: arrayOf(
    shape({
      direction: string,
      label: string,
      value: string,
      isActive: bool,
      id: string,
    }),
  ),
};

const DynamicContentListingApp = (props) => {
  const {
    id,
    title,
    subtitle,
    theme,
    enableQueryString,
    hideMobileSummary,
    url,
    featuredBackground,
    dictionary,
    query,
    link,
    sorters,
    pagination,
    loadMore,
  } = props;

  const activeSorterParam = useMemo(() => {
    const activeSorter = sorters.find((sorter) => sorter.isActive) || null;

    return activeSorter
      ? {
          sortBy: activeSorter.value,
          sortOrder: activeSorter.direction,
        }
      : null;
  }, [sorters]);

  const generalParams = {
    ...initialState.query,
    ...query,
    ...activeSorterParam,
  };

  const [showOverlay, setShowOverlay] = useState(false);

  const [selectedFacets, setSelectedFacets] = useState(
    initFacets(window.location.search),
  );
  const [serverData, setServerData] = useState(null);

  //initialize the query to that of the query string if it exists
  const [currentParams, setCurrentParams] = useState(
    initCurrentParams(window.location.search, generalParams),
  );

  const resultsFocusRef = useRef();
  const showFocusRef = useRef({ showFocus: false });

  useEffect(() => {
    if (new URL(document.location).searchParams.get("q")) {
      scrollToId(id, true);
      setTimeout(() => {
        document.querySelector(`#${id} .simple-search-box__input`).focus();
      }, 1000);
    }
  }, [id]);

  //update the url with the latest query parameters
  useEffect(() => {
    if (enableQueryString) {
      const queryString = createQueryString(currentParams, selectedFacets);
      if (queryString.length > 0) {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            "?" +
            createQueryString(currentParams, selectedFacets) +
            window.location.hash,
        );
      } else {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            window.location.hash,
        );
      }
    }
  }, [currentParams, enableQueryString, selectedFacets]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setShowOverlay(true);

    fetchSearchResults(
      url,
      {
        ...currentParams,
        ...selectedFacets,
      },
      signal,
    )
      .then(function (data) {
        if (data && loadMore && currentParams.page > 1) {
          setServerData((prevState) => {
            return {
              ...data,
              results: [...prevState.results, ...data.results],
            };
          });
        } else {
          setServerData(data);
        }
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return; // Continuation logic has already been skipped, so return normally
        }
        console.warn("request error: " + e.message);
      })
      .finally(function () {
        setShowOverlay(false);
      });
    // unmounts or data changes
    return () => {
      controller.abort();
    };
  }, [currentParams, selectedFacets, url, loadMore]);

  useEffect(() => {
    if (resultsFocusRef.current && showFocusRef.current.showFocus) {
      //move the focus back to the top of the results on page change after new results are rendred.
      window.setTimeout(() => {
        resultsFocusRef.current.focus();
        showFocusRef.current.showFocus = false;
      }, 100);
    }
  }, [serverData]);

  const handlePageChange = (page) => {
    showFocusRef.current.showFocus = true;
    setCurrentParams((prevState) => {
      return { ...prevState, page: page };
    });
    if (pagination) {
      scrollToId(`${id}-results`, true);
    }
  };

  const handleFacetChange = useCallback((facetGroupValue, facet) => {
    // analytics
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      type: "DCL Facet Change",
      event: "dropdownClick",
      dropdownSelection: facet,
    });

    setSelectedFacets((prevState) => {
      return toggleFacet(facetGroupValue, facet, prevState);
    });
  }, []);

  const handleClearFacetGroup = (facetGroupValue) => {
    setSelectedFacets((prevState) => {
      return clearFacetGroup(facetGroupValue, prevState);
    });
  };

  const handleSorterClick = (params) => {
    setCurrentParams((prevState) => {
      return { ...prevState, ...params, page: 1 };
    });
  };

  const showLoadMore =
    loadMore &&
    serverData &&
    serverData.totalResults > currentParams.page * currentParams.perPage &&
    theme != 6; //ThreeUpGrid only ever shows the first 3 

  return (
    <div className="dynamic-content-listing__app" id={id}>
      {serverData &&
        serverData.results &&
        serverData.results.length > 0 &&
        theme === 4 && (
          <div className="dynamic-content-listing__featured">
            <FeaturedPromo
              {...serverData.results[0]}
              focusRef={null}
              imageBackground={`${window.assetsFolder}/img/gray-rectangle.jpg 2340w, ${window.assetsFolder}/img/gray-rectangle.jpg 1170w`}
              ctaText=""
            />
          </div>
        )}
      {serverData && serverData.facets && serverData.facets.length > 0 && (
        <div className="dynamic-content-listing__filter-bar">
          <FilterBar
            filterTitle={dictionary.filterTitle}
            filterLabel={dictionary.filterLabel}
            onFacetChange={handleFacetChange}
            facets={serverData.facets}
            onClearFacetGroup={handleClearFacetGroup}
            selectedFacets={selectedFacets}
          />
        </div>
      )}
      {serverData && (
        <div className="dynamic-content-listing__sorters-wrapper">
          <div className="dynamic-content-listing__results-info">
            <ResultsInfo
              totalResults={serverData.totalResults}
              request={serverData.request}
              resultsInfoLabel={dictionary.resultsInfoLabel}
              query={currentParams.q}
              clearAllFacetsLabel={dictionary.clearAll}
            />
          </div>

          {sorters && sorters.length > 0 && (
            <div className="dynamic-content-listing__sorters">
              <Sorters
                onSorterClick={handleSorterClick}
                sorters={sorters}
                sortBy={currentParams.sortBy}
                sortDirection={currentParams.sortOrder}
              />
            </div>
          )}
        </div>
      )}
      {serverData && serverData.results && serverData.results.length < 1 && (
        <div className="dynamic-content-listing__no-results" aria-live="polite">
          {dictionary.noResultsMessage || "No Results Found"}
        </div>
      )}
      {serverData && serverData.results && (
        <div
          className={cx("dynamic-content-listing__results", {
            "dynamic-content-listing__results--no-mobile-summaries": hideMobileSummary,
          })}
          id={`${id}-results`}
        >
          {showOverlay && <LoadingSpinner />}
          {!showOverlay && serverData.results.length > 0 && (
            <div aria-live="polite" className="u-visuallyhidden">
              New Results
            </div>
          )}
          {(!theme || theme === 1) && (
            <SimpleGrid
              results={serverData.results}
              focusRef={resultsFocusRef}
            />
          )}
          {theme === 2 && (
            <SimpleGrid
              results={serverData.results}
              reversed={true}
              focusRef={resultsFocusRef}
            />
          )}
          {theme === 3 && (
            <SimpleList
              results={serverData.results}
              focusRef={resultsFocusRef}
            />
          )}
          {theme === 4 && (
            <FullGrid
              results={serverData.results.slice(1)}
              focusRef={currentParams.page > 1 ? resultsFocusRef : null}
              focusItem={(currentParams.page - 1) * currentParams.perPage}
            />
          )}
          {theme === 5 && (
            <CaseStudyGrid
              results={serverData.results}
              focusRef={resultsFocusRef}
              focusItem={(currentParams.page - 1) * currentParams.perPage}
            />
          )}
          {theme === 6 && (
            <ThreeUpGrid
              results={serverData.results}
              focusRef={resultsFocusRef}
            />
          )}
        </div>
      )}
      {showLoadMore && (
        <div className="dynamic-content-listing__load-more">
          <button
            onClick={() => handlePageChange(currentParams.page + 1)}
            className="btn btn--primary dynamic-content-listing__load-more-btn"
            type="button"
          >
            {dictionary.loadMoreLabel
              ? dictionary.loadMoreLabel
              : "Load more results"}
          </button>
        </div>
      )}
    </div>
  );
};

DynamicContentListingApp.propTypes = propTypes;
export default DynamicContentListingApp;
